import { Badge, BadgeTone, Icon } from '../ui';
import type { Prospect } from './DealCard';
import { AdsLine, adsReadout, exactMoney } from './meta';

interface AdsReadoutPanelProps {
  /** The card as the drawer is editing it, so an unsaved deal value re-prices the preview. */
  card: Prospect;
  closeRate: number | null;
  closeRateIsOverride: boolean;
}

const STATE_TONE: Record<AdsLine['state'], BadgeTone> = {
  sent: 'up',
  queued: 'warn',
  later: 'idle',
  never: 'neutral',
};

function Line({ name, line, note }: { name: string; line: AdsLine; note?: string }) {
  const muted = line.state === 'never';
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'baseline',
        gap: '2px var(--space-3)',
        padding: '10px 0',
        borderTop: '1px solid var(--border-hairline)',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'] }}>
          {name}
        </span>
        <Badge tone={STATE_TONE[line.state]}>{line.status}</Badge>
      </span>
      <span
        className="t-num"
        style={{
          fontSize: 'var(--text-md)',
          fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
          color: muted ? 'var(--text-faint)' : 'var(--text-body)',
          textAlign: 'right',
        }}
      >
        {line.value != null ? exactMoney(line.value) : '—'}
      </span>
      <span style={{ gridColumn: '1 / -1', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
        {line.basis}
        {note ? ` · ${note}` : ''}
      </span>
    </div>
  );
}

/**
 * Drawer readout: what Project Signal sent to Google Ads for this card, or what it will send.
 * Sent values are the ones recorded at upload; the rest are priced at today's close rate.
 */
export default function AdsReadoutPanel({ card, closeRate, closeRateIsOverride }: AdsReadoutPanelProps) {
  const r = adsReadout(card, closeRate);
  const pendingNote = (line: AdsLine | null, usesRate: boolean) =>
    line?.state === 'queued'
      ? 'goes at the next daily upload'
      : line?.state === 'later'
        ? usesRate
          ? `today's ${closeRateIsOverride ? 'override' : 'running'} rate · locked in when sent`
          : 'locked in when sent'
        : undefined;

  return (
    <div
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-raised)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
        <span className="t-eyebrow">Google Ads offline conversions</span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: 'var(--text-xs)',
            color: r.matchTone === 'ok' ? 'var(--text-faint)' : 'var(--status-warn)',
          }}
        >
          <Icon name={r.matchTone === 'ok' ? 'link' : 'triangle-alert'} size={12} />
          {r.match}
        </span>
      </div>

      {r.error ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--danger-400)', marginBottom: '8px' }}>
          Upload failed: {r.error}
        </div>
      ) : null}

      {r.blocked ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>{r.blocked}</div>
      ) : (
        <>
          {r.qualified ? <Line name="Qualified Lead" line={r.qualified} note={pendingNote(r.qualified, true)} /> : null}
          {r.closed ? <Line name="Closed Sale" line={r.closed} note={pendingNote(r.closed, false)} /> : null}
        </>
      )}
    </div>
  );
}
